import React from 'react';
import { Image } from '@sato/components';
import { isEmpty, omit } from 'lodash';
import { twMerge } from 'tailwind-merge';

import { isLocalOrTestEnv } from '@utils/featureFlags';

import {
  IImageFields,
  isContentType,
  MediaComponentProps,
  MediaEntry,
} from '../types';

import { VideoPlayer } from './VideoPlayer';

const aspectRatioMap = {
  original: '',
  video: 'aspect-video',
  square: 'aspect-square',
  panorama: 'w-full h-[15rem] md:h-[23rem]',
} satisfies Record<NonNullable<IImageFields['aspectRatio']>, string>;

export const ResolveMediaEntry = <T extends MediaEntry>({
  entry,
  options,
}: {
  entry: T;
  options?: MediaComponentProps;
}) => {
  const contentType = entry.contentType;

  if (isContentType(entry, 'image')) {
    const { allowFullScreen, aspectRatio, image, positioning, ...imageData } =
      entry;

    if (!image || isEmpty(image)) return null;

    return (
      <Image
        {...omit(imageData, 'contentType')}
        {...omit(image, 'srcType')}
        {...options}
        isExpandable={allowFullScreen}
        source="contentful"
        imgClasses="w-full"
        className={twMerge(
          'flex w-full',
          aspectRatio && aspectRatioMap[aspectRatio],
          positioning === 'center' && 'justify-center',
          positioning === 'right' && 'justify-end'
        )}
      />
    );
  } else if (isContentType(entry, 'video')) {
    return <VideoPlayer {...entry} {...options} poster={entry.poster?.src} />;
  }

  if (isLocalOrTestEnv) {
    return (
      <div>
        Missing component definition for: <strong>{contentType}</strong>
      </div>
    );
  }

  return null;
};
